/**
 * Accessibility Utilities
 * 
 * Provides helpers for ARIA labels, keyboard shortcuts, and screen reader support
 */

import { Platform } from 'react-native';

export const MIN_TOUCH_TARGET = 44;

type A11yStateValue = boolean | 'mixed' | undefined;

export type A11yState = {
  disabled?: A11yStateValue;
  selected?: A11yStateValue;
  busy?: A11yStateValue;
  expanded?: A11yStateValue;
  checked?: A11yStateValue;
};

export const buildA11yState = (state: A11yState): Record<string, boolean | 'mixed'> => {
  const nextState: Record<string, boolean | 'mixed'> = {};
  (['disabled', 'selected', 'busy', 'expanded', 'checked'] as const).forEach((key) => {
    const value = state[key];
    if (typeof value === 'boolean') {
      nextState[key] = value;
    } else if (key === 'checked' && value === 'mixed') {
      nextState[key] = value;
    }
  });
  return nextState;
};

export const getDismissA11yProps = (subject: string) => ({
  accessibilityRole: 'button' as const,
  accessibilityLabel: `Close ${subject}`,
  accessibilityHint: `Closes the ${subject} and returns to the previous screen`,
});

export const getSelectionA11yProps = (label: string, selected: boolean) => ({
  accessibilityRole: 'button' as const,
  accessibilityLabel: selected ? `${label}, selected` : label,
  accessibilityHint: selected ? 'Currently selected' : 'Double tap to select',
  accessibilityState: buildA11yState({ selected }),
});

export const getMeaningfulImageA11yProps = (description: string) => ({
  accessible: true,
  accessibilityLabel: description,
});

export const decorativeImageA11yProps = {
  accessible: false,
  importantForAccessibility: 'no-hide-descendants' as const,
};

/**
 * Generate accessible label for common actions
 */
export const getAccessibleLabel = (
  action: string,
  context?: string
): string => {
  if (context) {
    return `${action} ${context}`;
  }
  return action;
};

/**
 * Generate accessible hint for actions
 */
export const getAccessibleHint = (action: string, result: string): string => {
  return `${action}. ${result}`;
};

/**
 * Common accessibility labels
 */
export const AccessibilityLabels = {
  // Navigation
  back: 'Go back',
  close: 'Close',
  menu: 'Open menu',
  search: 'Search',
  filter: 'Filter',
  sort: 'Sort',

  // Trip actions
  createTrip: 'Create new trip',
  editTrip: 'Edit trip',
  deleteTrip: 'Delete trip',
  archiveTrip: 'Archive trip',
  shareTrip: 'Share trip',

  // Itinerary actions
  addItem: 'Add item to itinerary',
  editItem: 'Edit itinerary item',
  deleteItem: 'Delete itinerary item',
  viewItem: 'View itinerary item details',
  addToItinerary: 'Add to itinerary',

  // Map actions
  viewMap: 'View map',
  getDirections: 'Get directions',
  viewLocation: 'View location details',

  // Booking actions
  viewBooking: 'View booking details',
  syncBookings: 'Sync bookings from email',
  refreshBooking: 'Refresh booking status',

  // Collaboration
  inviteUser: 'Invite user to trip',
  voteOnItem: 'Vote on item',
  viewVotes: 'View votes',

  // Recommendations
  viewRecommendation: 'View recommendation',
  saveRecommendation: 'Save recommendation',
  addRecommendation: 'Add recommendation to itinerary',
};

/**
 * Common accessibility hints
 */
export const AccessibilityHints = {
  addToItinerary: 'Adds this place to your trip itinerary',
  getDirections: 'Opens directions in your maps app',
  viewDetails: 'Shows detailed information about this item',
  editItem: 'Allows you to modify this item',
  deleteItem: 'Permanently removes this item',
  shareTrip: 'Shares this trip with others',
  voteOnItem: 'Vote to include or exclude this item',
  syncBookings: 'Synchronizes bookings from your email',
};

/**
 * Keyboard shortcuts for web
 */
export const KeyboardShortcuts = {
  // Navigation
  search: 'Ctrl+K / Cmd+K',
  newTrip: 'Ctrl+N / Cmd+N',
  back: 'Escape',
  close: 'Escape',

  // Actions
  addItem: 'Ctrl+A / Cmd+A',
  save: 'Ctrl+S / Cmd+S',
  delete: 'Delete',
  edit: 'E',

  // View
  toggleMap: 'M',
  toggleCalendar: 'C',
  toggleList: 'L',
};

/**
 * Get keyboard shortcut hint for web
 */
export const getKeyboardShortcutHint = (action: keyof typeof KeyboardShortcuts): string => {
  if (Platform.OS !== 'web') {
    return '';
  }

  const shortcut = KeyboardShortcuts[action];
  const isMac = Platform.OS === 'web' && typeof navigator !== 'undefined' && 
    navigator.platform?.toUpperCase().indexOf('MAC') >= 0;
  
  return shortcut.replace(isMac ? 'Ctrl' : 'Cmd', isMac ? 'Cmd' : 'Ctrl');
};

/**
 * Screen reader announcements
 */
export class ScreenReader {
  /**
   * Announce message to screen reader (web only)
   */
  static announce(message: string, priority: 'polite' | 'assertive' = 'polite'): void {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const announcement = document.createElement('div');
      announcement.setAttribute('role', 'status');
      announcement.setAttribute('aria-live', priority);
      announcement.setAttribute('aria-atomic', 'true');
      announcement.className = 'sr-only';
      announcement.textContent = message;
      
      document.body.appendChild(announcement);
      
      setTimeout(() => {
        document.body.removeChild(announcement);
      }, 1000);
    }
  }

  /**
   * Announce success action
   */
  static announceSuccess(action: string): void {
    this.announce(`${action} completed successfully`, 'polite');
  }

  /**
   * Announce error
   */
  static announceError(error: string): void {
    this.announce(`Error: ${error}`, 'assertive');
  }

  /**
   * Announce loading state
   */
  static announceLoading(action: string): void {
    this.announce(`${action} in progress`, 'polite');
  }
}

/**
 * High contrast mode detection
 */
export const isHighContrastMode = (): boolean => {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    const prefersContrast = window.matchMedia('(prefers-contrast: high)');
    return prefersContrast.matches;
  }
  return false;
};

/**
 * Reduced motion detection
 */
export const prefersReducedMotion = (): boolean => {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    return prefersReduced.matches;
  }
  return false;
};

/**
 * Generate ARIA attributes for interactive elements
 */
export const getAriaAttributes = (options: {
  label?: string;
  hint?: string;
  role?: string;
  expanded?: boolean;
  selected?: boolean;
  disabled?: boolean;
  required?: boolean;
  invalid?: boolean;
}): Record<string, string | boolean> => {
  const attrs: Record<string, string | boolean> = {};

  if (options.label) {
    attrs['aria-label'] = options.label;
  }

  if (options.hint) {
    attrs['aria-describedby'] = options.hint;
  }

  if (options.role) {
    attrs['role'] = options.role;
  }

  if (options.expanded !== undefined) {
    attrs['aria-expanded'] = options.expanded;
  }

  if (options.selected !== undefined) {
    attrs['aria-selected'] = options.selected;
  }

  if (options.disabled !== undefined) {
    attrs['aria-disabled'] = options.disabled;
  }

  if (options.required !== undefined) {
    attrs['aria-required'] = options.required;
  }

  if (options.invalid !== undefined) {
    attrs['aria-invalid'] = options.invalid;
  }

  return attrs;
};

